import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Text } from "@chakra-ui/react";
import { fetchTrades } from "../../../services/tradeApi";
import { listBacktests, getBacktestTradeIds, dedupeBacktestsById } from "../../../services/backtestsApi";
import { backendTradeToFrontendTrade } from "../../../services/tradeStorage";
import type { Backtest } from "../../../types/backtests";
import type { Trade } from "../../../types/trade";
import {
  formatDateAsCalendarDayInTimeZone,
} from "../../../utils/notificationPeriod";
import {
  resolveReportTradeReviewFetchWindow,
  resolveReportTradeReviewRequestLocalWindow,
} from "../../../utils/reportTradeReviewWindow";
import { filterTradesExitedOnOrBefore } from "../../../utils/reportTradingPnlSeries";
import { ReportTradeReviewPanel } from "../Reports/ReportTradeReviewPanel";
import type { TradeReviewTabProps } from "../TradeLog/components/TradeReview/TradeReviewTab";
import {
  comparisonScopeReviewFingerprint,
  getComparisonRunSavedTitle,
  type ComparisonRun,
} from "./simulatorComparisonTypes";
import { SimulatorAlert } from "./SimulatorAlert";

function parseCalendarYmd(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (!m) return null;
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  const d = parseInt(m[3], 10);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
  return new Date(y, mo - 1, d, 0, 0, 0, 0);
}

function scopeToDateRange(scope: ComparisonRun["scope"]): { startDate: Date | null; endDate: Date | null } {
  if (scope.localStartDate && scope.localEndDate) {
    return {
      startDate: parseCalendarYmd(scope.localStartDate),
      endDate: parseCalendarYmd(scope.localEndDate),
    };
  }
  return { startDate: null, endDate: null };
}

function scopeToRequestWindow(scope: ComparisonRun["scope"]): {
  timeframe?: string;
  start_date?: string;
  end_date?: string;
} | undefined {
  if (scope.localStartDate && scope.localEndDate) {
    const start = parseCalendarYmd(scope.localStartDate);
    const end = parseCalendarYmd(scope.localEndDate);
    if (start && end) {
      const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999);
      return {
        start_date: new Date(start.getFullYear(), start.getMonth(), start.getDate()).toISOString(),
        end_date: endDay.toISOString(),
      };
    }
  }
  if (scope.timeframe) return { timeframe: scope.timeframe };
  return undefined;
}

function runHasReviewableResult(run: ComparisonRun): boolean {
  return run.status !== "failed" && !run.error && (run.status === "succeeded" || run.result != null);
}

function policyOverlayFromRun(run: ComparisonRun | null): {
  kept_trade_ids: string[];
  removed_trade_ids: string[];
} | null {
  if (!run?.result) return null;
  const kept = run.result.kept_trade_ids ?? [];
  const removed = run.result.removed_trade_ids ?? [];
  if (kept.length === 0 && removed.length === 0) return null;
  return { kept_trade_ids: kept, removed_trade_ids: removed };
}

export type SimulatorComparisonTradeReviewPanelProps = {
  brokerAccountCode: string;
  reportingTimeZone: string;
  subtleText: string;
  reportId?: string | null;
  /** Report generation instant — freeze Trade Review to that cohort. */
  reportCreatedAt?: string | null;
  prefillWindow?: {
    start_date?: string | null;
    end_date?: string | null;
    timeframe?: string | null;
  } | null;
  defaultCandleTimeframe?: "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | "1d";
  comparisonRuns: ComparisonRun[];
  selectedRun: ComparisonRun | null;
  simulationOverlayMode?: string;
  /** Parent-provided list (report embed) — skips a duplicate GET /backtests. */
  linkedBacktests?: Backtest[];
  /** Bump when a run is saved so linked simulations refresh (only when fetching locally). */
  linkedBacktestsRefreshKey?: number;
};

function SimulatorComparisonTradeReviewPanelInner(
  props: SimulatorComparisonTradeReviewPanelProps
): React.ReactElement {
  const {
    brokerAccountCode,
    reportingTimeZone,
    subtleText,
    reportId,
    reportCreatedAt,
    prefillWindow,
    defaultCandleTimeframe,
    comparisonRuns,
    selectedRun,
    simulationOverlayMode,
    linkedBacktests: linkedBacktestsProp,
    linkedBacktestsRefreshKey = 0,
  } = props;

  const usesParentLinkedBacktests = linkedBacktestsProp != null;

  const scopeSourceRun = useMemo(() => {
    const reviewable = comparisonRuns.find(runHasReviewableResult);
    return reviewable ?? selectedRun ?? comparisonRuns[0] ?? null;
  }, [comparisonRuns, selectedRun]);

  const scopeFingerprint = useMemo(
    () => (scopeSourceRun ? comparisonScopeReviewFingerprint(scopeSourceRun.scope) : ""),
    [scopeSourceRun]
  );

  const requestWindow = useMemo(() => {
    if (scopeSourceRun) return scopeToRequestWindow(scopeSourceRun.scope);
    if (prefillWindow?.start_date && prefillWindow?.end_date) {
      return {
        start_date: prefillWindow.start_date ?? undefined,
        end_date: prefillWindow.end_date ?? undefined,
      };
    }
    if (prefillWindow?.timeframe) return { timeframe: prefillWindow.timeframe };
    return undefined;
  }, [prefillWindow, scopeSourceRun]);

  const recordedTimezoneLabel = scopeSourceRun?.scope?.timezone ?? undefined;

  const [trades, setTrades] = useState<Trade[]>([]);
  const [tradesLoading, setTradesLoading] = useState(false);
  const [tradesError, setTradesError] = useState<string | null>(null);

  const [linkedBacktestsLocal, setLinkedBacktestsLocal] = useState<Backtest[]>([]);
  const [overlayBacktestId, setOverlayBacktestId] = useState("");
  const [overlayEnabled, setOverlayEnabled] = useState(true);
  const [overlayTradeIdsLoading, setOverlayTradeIdsLoading] = useState(false);
  const [overlayTradeIdsError, setOverlayTradeIdsError] = useState<string | null>(null);
  const [overlayTradeIds, setOverlayTradeIds] = useState<{
    kept: string[];
    removed: string[];
    source?: string;
  } | null>(null);

  const loadLinkedBacktests = useCallback(async () => {
    const rid = reportId?.trim();
    if (!rid || !brokerAccountCode.trim()) {
      setLinkedBacktestsLocal([]);
      return;
    }
    try {
      const resp = await listBacktests({
        broker_account_code: brokerAccountCode,
        report_id: rid,
        status: undefined,
        limit: 50,
        offset: 0,
      });
      setLinkedBacktestsLocal(Array.isArray(resp?.backtests) ? resp.backtests : []);
    } catch {
      setLinkedBacktestsLocal([]);
    }
  }, [brokerAccountCode, reportId]);

  useEffect(() => {
    if (usesParentLinkedBacktests) return;
    void loadLinkedBacktests();
  }, [loadLinkedBacktests, linkedBacktestsRefreshKey, usesParentLinkedBacktests]);

  const linkedBacktests = usesParentLinkedBacktests ? linkedBacktestsProp! : linkedBacktestsLocal;

  const overlayOptions = useMemo(() => {
    const byId = new Map<string, Backtest>();
    for (const b of dedupeBacktestsById(linkedBacktests)) {
      if (b?.id) byId.set(b.id, b);
    }
    for (const run of comparisonRuns) {
      const id = run.backtestId?.trim();
      if (!id) continue;
      const existing = byId.get(id);
      if (existing?.result != null) continue;
      byId.set(id, {
        ...(existing ?? {}),
        id,
        title: getComparisonRunSavedTitle(run),
        result: run.result ?? existing?.result,
        scope: existing?.scope,
      } as Backtest);
    }
    return Array.from(byId.values());
  }, [comparisonRuns, linkedBacktests]);

  const selectedRunInlineOverlay = useMemo(
    () => policyOverlayFromRun(selectedRun),
    [selectedRun]
  );

  /** Follow the active simulation tab; dropdown overrides until the user switches tabs. */
  useEffect(() => {
    const bid = selectedRun?.backtestId?.trim() ?? "";
    setOverlayBacktestId(bid);
    setOverlayEnabled(true);
    setOverlayTradeIds(null);
    setOverlayTradeIdsError(null);
  }, [selectedRun?.id]);

  useEffect(() => {
    setOverlayTradeIds(null);
    setOverlayTradeIdsError(null);
  }, [overlayBacktestId]);

  useEffect(() => {
    if (!overlayEnabled || !overlayBacktestId) return;
    if (overlayTradeIds || overlayTradeIdsLoading) return;

    let cancelled = false;
    const requestedId = overlayBacktestId;
    void (async () => {
      setOverlayTradeIdsLoading(true);
      setOverlayTradeIdsError(null);
      try {
        const resp = await getBacktestTradeIds(requestedId);
        if (cancelled || requestedId !== overlayBacktestId) return;
        setOverlayTradeIds({
          kept: Array.isArray(resp?.kept_trade_ids) ? resp.kept_trade_ids : [],
          removed: Array.isArray(resp?.removed_trade_ids) ? resp.removed_trade_ids : [],
          source: typeof resp?.source === "string" ? resp.source : undefined,
        });
      } catch (e) {
        if (cancelled || requestedId !== overlayBacktestId) return;
        setOverlayTradeIds(null);
        setOverlayTradeIdsError(e instanceof Error ? e.message : "Failed to load simulation trade IDs.");
      } finally {
        if (!cancelled) setOverlayTradeIdsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Do not depend on overlayTradeIdsLoading — setting it true would cancel the in-flight request
    // and leave the banner stuck on “Loading kept vs removed overlay…”.
  }, [overlayBacktestId, overlayEnabled, overlayTradeIds]);

  const policyOverlay = useMemo(() => {
    // Hybrid EP+RA: keep kept/removed even when simulationOverlayMode is baseline_vs_simulated.
    if (!overlayEnabled) return null;
    const fromApi = overlayTradeIds
      ? {
          kept_trade_ids: overlayTradeIds.kept,
          removed_trade_ids: overlayTradeIds.removed,
        }
      : null;
    const fromRun = selectedRunInlineOverlay;
    if (fromApi && fromRun) {
      return {
        kept_trade_ids: fromApi.kept_trade_ids.length ? fromApi.kept_trade_ids : fromRun.kept_trade_ids,
        removed_trade_ids: fromApi.removed_trade_ids.length
          ? fromApi.removed_trade_ids
          : fromRun.removed_trade_ids,
      };
    }
    if (fromApi) return fromApi;
    if (!overlayBacktestId && fromRun) return fromRun;
    if (fromRun) return fromRun;
    return null;
  }, [overlayBacktestId, overlayEnabled, overlayTradeIds, selectedRunInlineOverlay]);

  const realReportId = reportId?.trim() || undefined;

  const reportCreatedAsOfExactMs = useMemo(() => {
    const raw = reportCreatedAt?.trim();
    if (!raw) return null;
    const ms = Date.parse(raw);
    return Number.isFinite(ms) ? ms : null;
  }, [reportCreatedAt]);

  const reportCreatedAsOfYmd = useMemo(() => {
    if (reportCreatedAsOfExactMs == null) return null;
    return formatDateAsCalendarDayInTimeZone(
      new Date(reportCreatedAsOfExactMs),
      reportingTimeZone
    );
  }, [reportCreatedAsOfExactMs, reportingTimeZone]);

  const backtestIdForChart =
    overlayEnabled && overlayBacktestId.trim()
      ? overlayBacktestId.trim()
      : scopeSourceRun?.backtestId?.trim() ||
        selectedRun?.backtestId?.trim() ||
        undefined;

  const chartRequestWindow = useMemo((): TradeReviewTabProps["requestWindow"] | undefined => {
    const scope = scopeSourceRun?.scope;
    const dateRange = scopeSourceRun
      ? scopeToDateRange(scopeSourceRun.scope)
      : {
          startDate: prefillWindow?.start_date
            ? parseCalendarYmd(String(prefillWindow.start_date).slice(0, 10))
            : null,
          endDate: prefillWindow?.end_date
            ? parseCalendarYmd(String(prefillWindow.end_date).slice(0, 10))
            : null,
        };
    const timeframe =
      scope?.timeframe ??
      (scope?.localStartDate && scope?.localEndDate ? "" : undefined) ??
      prefillWindow?.timeframe ??
      "all-time";

    if (scope?.localStartDate && scope?.localEndDate) {
      const end =
        reportCreatedAsOfYmd && scope.localEndDate > reportCreatedAsOfYmd
          ? reportCreatedAsOfYmd
          : scope.localEndDate;
      return {
        local_start_date: scope.localStartDate,
        local_end_date: end,
        timezone: reportingTimeZone,
      };
    }

    return resolveReportTradeReviewRequestLocalWindow({
      dateRange,
      timeframe: timeframe || "all-time",
      reportingTimeZone,
      asOfYmd: reportCreatedAsOfYmd,
    });
  }, [
    scopeSourceRun,
    prefillWindow,
    reportingTimeZone,
    reportCreatedAsOfYmd,
  ]);

  const symbolDiscovery = useMemo(() => {
    // Prefer backtest discovery when a sim is on the chart — linked report may 404.
    const bid =
      backtestIdForChart ??
      scopeSourceRun?.backtestId?.trim() ??
      selectedRun?.backtestId?.trim();
    if (bid) return { kind: "backtest" as const, id: bid };
    if (realReportId) return { kind: "report" as const, id: realReportId };
    return undefined;
  }, [realReportId, backtestIdForChart, scopeSourceRun?.backtestId, selectedRun?.backtestId]);

  const overlaySimulationRows = useMemo(() => {
    const bid = overlayBacktestId.trim();
    if (!bid) return undefined;
    const run =
      comparisonRuns.find((r) => r.backtestId?.trim() === bid) ??
      (selectedRun?.backtestId?.trim() === bid ? selectedRun : null);
    const fromRun = run?.result?.simulated_trades;
    if (Array.isArray(fromRun) && fromRun.length > 0) return fromRun;
    const fromLinked = overlayOptions.find((b) => b.id === bid)?.result?.simulated_trades;
    return Array.isArray(fromLinked) && fromLinked.length > 0 ? fromLinked : undefined;
  }, [comparisonRuns, overlayBacktestId, overlayOptions, selectedRun]);

  const dateRangeForFetch = useMemo(
    () => (scopeSourceRun ? scopeToDateRange(scopeSourceRun.scope) : { startDate: null, endDate: null }),
    [scopeSourceRun]
  );
  const timeframeForFetch = scopeSourceRun?.scope?.timeframe ?? prefillWindow?.timeframe ?? "all-time";
  const scopeSymbol = scopeSourceRun?.scope?.symbol?.trim() ?? "";

  useEffect(() => {
    if (!scopeSourceRun || !brokerAccountCode.trim()) {
      setTrades([]);
      setTradesError(null);
      setTradesLoading(false);
      return;
    }

    let cancelled = false;
    void (async () => {
      setTradesLoading(true);
      setTradesError(null);
      try {
        const win = resolveReportTradeReviewFetchWindow({
          dateRange: dateRangeForFetch,
          timeframe: timeframeForFetch,
          reportingTimeZone,
          asOfYmd: reportCreatedAsOfYmd,
        });
        const opts: Parameters<typeof fetchTrades>[0] = {
          broker_account_code: brokerAccountCode,
          status: "closed",
          cache_bust: Date.now(),
        };
        if (scopeSymbol) opts.symbol = scopeSymbol;
        if (win.kind === "local") {
          opts.local_period = win.period;
        } else {
          opts.timeframe = win.timeframe;
        }
        const response = await fetchTrades(opts);
        if (cancelled) return;
        const list = Array.isArray(response?.trades)
          ? filterTradesExitedOnOrBefore(
              response.trades.map(backendTradeToFrontendTrade),
              reportCreatedAsOfExactMs
            )
          : [];
        setTrades(list);
      } catch (e) {
        if (cancelled) return;
        setTrades([]);
        setTradesError(e instanceof Error ? e.message : "Failed to load trades for review.");
      } finally {
        if (!cancelled) setTradesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    brokerAccountCode,
    dateRangeForFetch,
    reportingTimeZone,
    reportCreatedAsOfExactMs,
    reportCreatedAsOfYmd,
    scopeFingerprint,
    scopeSourceRun,
    scopeSymbol,
    timeframeForFetch,
  ]);

  const handleOverlayBacktestIdChange = useCallback((id: string) => {
    setOverlayBacktestId(id);
    setOverlayTradeIds(null);
    setOverlayTradeIdsError(null);
  }, []);

  const showReviewBody = scopeSourceRun != null && runHasReviewableResult(scopeSourceRun);

  if (!showReviewBody) {
    if (selectedRun?.error) {
      return (
        <SimulatorAlert tone="warning">
          Fix the simulation error above to open trade review.
        </SimulatorAlert>
      );
    }
    return (
      <Text fontSize="sm" color={subtleText} fontFamily="'IBM Plex Sans', sans-serif">
        Complete a simulation run to open trade review for this scope.
      </Text>
    );
  }

  if (!brokerAccountCode.trim()) {
    return (
      <SimulatorAlert tone="warning">
        Select a broker account in scope to load trade review.
      </SimulatorAlert>
    );
  }

  const candleTf =
    scopeSourceRun?.scope?.candleTimeframe ?? defaultCandleTimeframe ?? "1m";

  const cohortTradeIds = useMemo(() => {
    if (!policyOverlay) return null;
    const ids = [
      ...(policyOverlay.kept_trade_ids ?? []),
      ...(policyOverlay.removed_trade_ids ?? []),
    ]
      .map((id) => String(id ?? "").trim())
      .filter(Boolean);
    return ids.length > 0 ? ids : null;
  }, [policyOverlay]);

  const tradesForReview = useMemo(() => {
    if (!cohortTradeIds) return trades;
    const allowed = new Set(cohortTradeIds.map((id) => id.toLowerCase()));
    return trades.filter((t) => allowed.has(String(t.id ?? "").trim().toLowerCase()));
  }, [cohortTradeIds, trades]);

  // Candles cohort: prefer backtest_id for simulator runs (docs). A linked
  // report_id alone fails when the report is gone ("Report wasn't found") even
  // though the sim still stores report_id and kept/removed load fine.
  const candlesUseBacktest = Boolean(backtestIdForChart);
  return (
    <ReportTradeReviewPanel
      brokerAccountCode={brokerAccountCode}
      trades={tradesForReview}
      tradesLoading={tradesLoading}
      tradesError={tradesError}
      reportId={realReportId ?? symbolDiscovery?.id ?? "simulator"}
      reportIdForCohort={candlesUseBacktest ? undefined : realReportId}
      symbolDiscovery={symbolDiscovery}
      cohortIntent={candlesUseBacktest ? "backtest" : realReportId ? "report" : "backtest"}
      chartRequestWindow={chartRequestWindow}
      reportTimeframe={requestWindow?.timeframe ?? prefillWindow?.timeframe ?? undefined}
      reportStartDate={requestWindow?.start_date ?? prefillWindow?.start_date ?? undefined}
      reportEndDate={requestWindow?.end_date ?? prefillWindow?.end_date ?? undefined}
      reportTimezone={reportingTimeZone}
      asOfEndMs={reportCreatedAsOfExactMs}
      defaultCandleTimeframe={candleTf}
      recordedTimezoneLabel={recordedTimezoneLabel}
      linkedBacktests={overlayOptions}
      overlayBacktestId={overlayBacktestId}
      onOverlayBacktestIdChange={handleOverlayBacktestIdChange}
      overlayEnabled={overlayEnabled}
      onOverlayEnabledChange={setOverlayEnabled}
      overlayTradeIdsLoading={overlayTradeIdsLoading}
      overlayTradeIdsError={overlayTradeIdsError}
      overlayTradeIds={overlayTradeIds}
      policyOverlay={policyOverlay}
      backtestId={backtestIdForChart}
      simulationOverlayMode={simulationOverlayMode}
      backtestSimulationRows={overlaySimulationRows}
      simulatedLegs={selectedRun?.simulatedLegs ?? scopeSourceRun?.simulatedLegs}
      subtleText={subtleText}
    />
  );
}

export const SimulatorComparisonTradeReviewPanel = React.memo(SimulatorComparisonTradeReviewPanelInner);
